import { fetchQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { useCallback } from 'react'
import { useApplyAmendmentToQuote } from '../SelectScheduleForAmendment/useApplyAmendmentToQuote'
import { getAmendedScheduleUnavailableReason } from './useAmendedSchedule'

/**
 * Rebuilds the quote from the amended schedule at a new effective date, the
 * same way the amendment modal does, so any pricing or phase edits are lost.
 * Resolves to whether it worked, so the confirm modal can stay open for a retry.
 */
export const useChangeAmendmentEffectiveFrom = () => {
  const { displayNotification } = useNotifications()
  const rebuildAtDate = useRebuildAtDate()

  return useCallback(
    async (effectiveFrom: Date): Promise<boolean> => {
      // Splitting the phases throws if the date falls outside the schedule, so
      // a failed rebuild can reject as well as resolve to false.
      const isApplied = await rebuildAtDate(effectiveFrom).catch(() => false)

      if (!isApplied) {
        displayNotification('Unable to change the start date', {
          type: 'error'
        })
        return false
      }

      displayNotification('Schedule amendment applied to quote', {
        type: 'success'
      })
      return true
    },
    [displayNotification, rebuildAtDate]
  )
}

const useRebuildAtDate = () => {
  const cubeContext = useCubeContext()
  const billingScheduleId =
    cubeContext.queries.rawData.data.quote.amendment?.billingScheduleId
  const applyAmendmentToQuote = useApplyAmendmentToQuote()

  return useCallback(
    async (effectiveFrom: Date) => {
      if (!billingScheduleId) {
        return false
      }

      // Fetched fresh rather than from the cache the control reads, so the
      // rebuild uses the schedule as it is now.
      const schedule = await fetchQuery(
        apiQueryClient,
        dashboard20240730Client.getBillingSchedule,
        { id: billingScheduleId },
        { staleTime: 0 }
      )

      if (!schedule || getAmendedScheduleUnavailableReason(schedule)) {
        return false
      }

      return applyAmendmentToQuote({ schedule, amendmentDate: effectiveFrom })
    },
    [applyAmendmentToQuote, billingScheduleId]
  )
}
